import { useState } from 'react';
import { CheckIcon, ChevronLeftIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { AlignerMonitoringCell, Tooltip } from '@odo/shared-ui';
import { useIsTextTruncated } from '../../../../../hooks/useIsTextTruncated';
import { sendActivationLink, type DmPatient } from '../../../../../services/dmPatient';
import { Spinner, TOOLTIP_OVER_MODAL } from './DmControlPrimitives';
import { Fact, Muted } from './DmFact';
import {
    formatDmDate,
    monitoringState,
    treatmentLines,
    type MonitoringState
} from './dmControlUtils';
import { ScanboxFact } from './ScanboxFact';
import { useDmControlMutation } from './useDmControlMutation';

function Divider() {
    return <div className="h-10 w-px shrink-0 bg-gray-200" aria-hidden />;
}

function SendActivationLink({ patientId }: { patientId: string }) {
    const send = useDmControlMutation(patientId, () => sendActivationLink(patientId));
    if (send.saved) {
        return (
            <Tooltip content="Activation link sent" className={TOOLTIP_OVER_MODAL} focusable>
                <span className="inline-flex shrink-0 items-center text-[#007E87]">
                    <CheckIcon className="h-4 w-4" />
                </span>
            </Tooltip>
        );
    }
    const tip = send.error ? `Couldn't send: ${send.error}` : 'Send app activation link';
    return (
        <Tooltip content={tip} className={TOOLTIP_OVER_MODAL}>
            <button
                type="button"
                onClick={() => send.run()}
                disabled={send.saving}
                aria-label="Send app activation link"
                className={`inline-flex shrink-0 items-center transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${
                    send.error ? 'text-red-600' : 'text-[#007E87] hover:text-[#00656C]'
                }`}
            >
                {send.saving ? (
                    <Spinner className="h-4 w-4" />
                ) : (
                    <PaperAirplaneIcon className="h-4 w-4 -translate-y-0.5 -rotate-45" />
                )}
            </button>
        </Tooltip>
    );
}

// Same look as the dashboard's DM App field: a coloured dot and label, plus the send icon.
function PatientApp({ patientId, activated }: { patientId: string; activated: boolean }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span
                className={`h-2 w-2 shrink-0 rounded-full ${activated ? 'bg-emerald-500' : 'bg-red-500'}`}
            />
            <span className={activated ? 'text-emerald-700' : 'text-red-700'}>
                {activated ? 'Activated' : 'Not activated'}
            </span>
            {!activated && (
                <>
                    <span className="text-gray-300" aria-hidden>
                        ·
                    </span>
                    <SendActivationLink patientId={patientId} />
                </>
            )}
        </span>
    );
}

function stateLine(state: MonitoringState, patient: DmPatient): string {
    if (state === 'active') {
        const started = formatDmDate(patient.startDate);
        return started ? `Since ${started}` : 'Monitoring active';
    }
    if (state === 'paused') {
        const resumes = formatDmDate(patient.resumeDate);
        return resumes ? `Resumes ${resumes}` : 'Paused';
    }
    if (state === 'stopped') {
        const stopped = formatDmDate(patient.stopDate);
        return stopped ? `Stopped ${stopped}` : 'Monitoring ended';
    }
    return 'Monitoring not started yet';
}

interface SummaryProps {
    patient: DmPatient;
    state: MonitoringState;
}

function AlignerSummary({ patient, state }: SummaryProps) {
    const current = patient.currentAligner ?? null;
    const total = patient.totalAligners ?? null;
    return (
        <div className="flex shrink-0 items-center gap-3">
            <AlignerMonitoringCell
                current={current}
                total={total}
                monitoringStatus={patient.status}
            />
            <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-800">
                    {current ? `Aligner ${current}` : 'No aligner yet'}
                    {current && total ? (
                        <span className="font-normal text-gray-500"> of {total}</span>
                    ) : null}
                </div>
                <div className="mt-0.5 text-xs text-gray-500">{stateLine(state, patient)}</div>
            </div>
        </div>
    );
}

const NO_TREATMENT = { upper: null, lower: null };

function TreatmentLine({ label, text }: { label: string; text: string }) {
    const [element, setElement] = useState<HTMLElement | null>(null);
    const truncated = useIsTextTruncated(element, text);
    return (
        <Tooltip content={text} className={TOOLTIP_OVER_MODAL} disabled={!truncated}>
            <div ref={setElement} className="truncate text-[13px] text-gray-800">
                <span className="text-gray-500">{label}:</span> {text}
            </div>
        </Tooltip>
    );
}

// Fixed appliances have no aligners to count, so DM's treatment per arch takes their place.
function TreatmentSummary({ patient, state }: SummaryProps) {
    return (
        <div className="min-w-0 max-w-xs">
            {treatmentLines(patient.treatment ?? NO_TREATMENT).map(({ label, text }) => (
                <TreatmentLine key={label} label={label} text={text} />
            ))}
            <div className="mt-0.5 text-xs text-gray-500">{stateLine(state, patient)}</div>
        </div>
    );
}

interface DmFactsStripProps {
    patientId: string;
    patient: DmPatient;
    /** Shown on a page, where there is no modal header to go back from. */
    onBack?: () => void;
}

// The top of the control: the fixed DM facts, then the aligner gauge or the treatment per arch.
export function DmFactsStrip({ patientId, patient, onBack }: DmFactsStripProps) {
    const state = monitoringState(patient.status);
    return (
        <div className="flex items-center gap-6 py-5">
            {onBack && (
                <button
                    type="button"
                    onClick={onBack}
                    aria-label="Back"
                    className="-ml-2 shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                    <ChevronLeftIcon className="h-5 w-5" />
                </button>
            )}
            <dl className="flex min-w-0 flex-1 items-center gap-5">
                <Fact label="Plan" fullText={patient.productName}>
                    {patient.productName ?? <Muted>Not set</Muted>}
                </Fact>
                <Divider />
                <Fact label="Protocol" fullText={patient.protocolTitle}>
                    {patient.protocolTitle ?? <Muted>Set by quickstart</Muted>}
                </Fact>
                <Divider />
                <Fact label="Patient app">
                    <PatientApp patientId={patientId} activated={patient.isAppActivated === true} />
                </Fact>
                <Divider />
                <ScanboxFact patientId={patientId} patient={patient} />
            </dl>
            <Divider />
            {patient.tracksAligners !== false ? (
                <AlignerSummary patient={patient} state={state} />
            ) : (
                <TreatmentSummary patient={patient} state={state} />
            )}
        </div>
    );
}
